/**
 * @file Redux slice for the payment update page.
 * A `*_FAILURE` maps each refused wire field onto the state key whose input renders it.
 */
import Constants from "../../Action/Constants";
import { ActionInterface } from "../../Interface/ActionInterface";

interface StateInterface {
  isFetching: boolean;
  fields: string[];
  translatedMessage: string;
  payment: any;
};

const initialState: StateInterface = {
  isFetching: false,
  fields: [],
  translatedMessage: "",
  payment: null
};

export const paymentUpdateReducer = (state: StateInterface = initialState, action: ActionInterface): StateInterface => {
  switch (action.type) {
    case Constants.GLOBAL_PAYMENT_UPDATE_REQUEST:
      return {
        ...state,
        isFetching: true
      };

    case Constants.GLOBAL_PAYMENT_UPDATE_SUCCESS:
      return {
        ...state,
        isFetching: false,
        payment: action.payload?.payment
      };

    case Constants.GLOBAL_PAYMENT_UPDATE_FIELD_SUCCESS:
      return {
        ...state,
        isFetching: false,
        payment: action.payload?.payment
      };

    case Constants.GLOBAL_CHARGE_UPDATE_REQUEST:
      return {
        ...state,
        isFetching: false
      };

    case Constants.GLOBAL_CHARGE_UPDATE_SUCCESS:
      return {
        ...state,
        isFetching: false
      };

    case Constants.GLOBAL_PAYMENT_UPDATE_FAILURE:
      const paymentFields: string[] = [];

      if (action.payload?.err.fields.includes("amount")) {
        paymentFields.push("amountForPayment");
      }

      if (action.payload?.err.fields.includes("currency")) {
        paymentFields.push("currencyIdForPayment");
      }

      if (action.payload?.err.fields.includes("dateOfThePayment")) {
        paymentFields.push("dateOfThePayment");
      }

      if (action.payload?.err.fields.includes("firstDateOfStatementPeriod")) {
        paymentFields.push("firstDateOfStatementPeriodForPayment");
      }

      if (action.payload?.err.fields.includes("agencyBankAccount")) {
        paymentFields.push("agencyBankAccountIdForPayment");
      }

      if (action.payload?.err.fields.includes("baseBankAccount")) {
        paymentFields.push("baseBankAccountForPayment");
      }

      if (action.payload?.err.fields.includes("baseBankAccountName")) {
        paymentFields.push("baseBankAccountNameForPayment");
      }

      if (action.payload?.err.fields.includes("baseBankAccountId")) {
        paymentFields.push("agencyBankAccountIdForPayment");
      }

      if (action.payload?.err.fields.includes("contactId")) {
        paymentFields.push("selectedPersonForPayment");
      }

      if (action.payload?.err.fields.includes("companyId")) {
        paymentFields.push("selectedCompanyForPayment");
      }

      if (action.payload?.err.fields.includes("description")) {
        paymentFields.push("descriptionForPayment");
      }

      if (action.payload?.err.fields.includes("mainBankAccountId")) {
        paymentFields.push("counterpartyIbanForPayment");
      }

      return {
        ...state,
        isFetching: false,
        fields: paymentFields,
        translatedMessage: action.payload?.err.translatedMessage
      };

    case Constants.GLOBAL_CHARGE_CREATE_FAILURE:
      const chargeFields: string[] = [];

      if (action.payload?.err.fields.includes("invoiceDate")) {
        chargeFields.push("invoiceDateForCharge");
      }

      if (action.payload?.err.fields.includes("invoiceNumber")) {
        chargeFields.push("invoiceNumberForCharge");
      }

      if (action.payload?.err.fields.includes("amount")) {
        chargeFields.push("amountForCharge");
      }

      if (action.payload?.err.fields.includes("currency")) {
        chargeFields.push("currencyIdForCharge");
      }

      if (action.payload?.err.fields.includes("vat")) {
        chargeFields.push("vatForCharge");
      }

      if (action.payload?.err.fields.includes("type")) {
        chargeFields.push("typeIdForCharge");
      }

      if (action.payload?.err.fields.includes("contactId")) {
        chargeFields.push("selectedPersonForCharge");
      }

      if (action.payload?.err.fields.includes("companyId")) {
        chargeFields.push("selectedCompanyForCharge");
      }

      if(action.payload?.err.fields.includes("objectId")) {
        chargeFields.push("objectId")
      }

      if (action.payload?.err.message === "Charge requires at least one relation"
          || (action.payload?.err.fields.indexOf("propertyId") > -1 &&
              action.payload?.err.fields.indexOf("unitId") > -1 &&
                action.payload?.err.fields.indexOf("contractId") > -1)) {
        chargeFields.push("objectId");
      }

      if (action.payload?.err.fields.includes("firstDate")) {
        chargeFields.push("firstDateForCharge");
      }

      if (action.payload?.err.fields.includes("description")) {
        chargeFields.push("descriptionForCharge");
      }

      return {
        ...state,
        isFetching: false,
        fields: chargeFields,
        translatedMessage: action.payload?.err.translatedMessage
      };

    case Constants.GLOBAL_CHARGE_TYPE_CREATE_FAILURE:
      return {
        ...state,
        isFetching: false,
        fields: action.payload?.err.fields || [],
        translatedMessage: action.payload?.err.translatedMessage
      };

    case Constants.GLOBAL_BANK_ACCOUNT_CREATE_FAILURE:
      return {
        ...state,
        isFetching: false,
        fields: action.payload?.err.fields || [],
        translatedMessage: action.payload?.err.translatedMessage
      };

      case Constants.PAGE_CONTACT_COMPANY_CREATE_FAILURE:
      return {
        ...state,
        isFetching: false,
        fields: action.payload?.err.fields || [],
        translatedMessage: action.payload?.err.translatedMessage
      };


    case Constants.PAGE_CONTACT_PERSON_CREATE_FAILURE:
      return {...state,
        isFetching: false,
        fields: action.payload?.err.fields || [],
        translatedMessage: action.payload?.err.translatedMessage,
      };

    case Constants.PAGE_CONTACT_PERSON_CREATE_CLEAR:
    case Constants.PAGE_CONTACT_COMPANY_CREATE_CLEAR:
    case Constants.GLOBAL_PAYMENT_UPDATE_CLEAR:
    case Constants.GLOBAL_CHARGE_CREATE_CLEAR:
    case Constants.GLOBAL_CHARGE_TYPE_CREATE_CLEAR:
    case Constants.GLOBAL_BANK_ACCOUNT_CREATE_CLEAR:
    case Constants.AUTH_SIGNOUT:
      return {
        ...initialState
      };

    default:
      return state;
  }
};
